import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Container, Row, Col, Form } from 'react-bootstrap';
import CustomCloseButton from '../Components/CustomCloseButton';
import { useAuth } from '../Context/AuthContext';
import { toast } from 'react-toastify';

import { db } from '../firebase-config';
import { doc, runTransaction } from 'firebase/firestore';

const FinalizeRequestModal = (props)=>{
    const { setShowSpinner } = useAuth();
    const [truckChargeWarning, setTruckChargeWarning] = useState("");
    const [submittedHoursWarning, setSubmittedHoursWarning] = useState("");
    const [techBlankHoursWarning, setTechBlankHoursWarning] = useState([]);
    const [techBlankRatesWarning, setTechBlankRatesWarning] = useState([]);
    const [unitsWarning, setUnitsWarning] = useState([]);
    const [materialsWarning, setMaterialsWarning] = useState([]);

    const [quoteNo, setQuoteNo] = useState("");
    const [quoteAmount, setQuoteAmount] = useState(0.00);
    const [truckCharge, setTruckCharge] = useState(0.00);

    const [hours, setHours] = useState([]);
    const [assignedTechnicians, setAssignedTechnicians] = useState([]);
    const [materials, setMaterials] = useState([]);

    const [printInvoiceNumber, setPrintInvoiceNumber] = useState(null);
    const [printQuoteNumber, setPrintQuoteNumber] = useState(null);
    const [printMaterials, setPrintMaterials] = useState(null);
    const [printMaterialCharges, setPrintMaterialCharges] = useState(null);
    const [printTruckCharge, setPrintTruckCharge] = useState(null);
    const [printHours, setPrintHours] = useState(null);


    const finalize = ()=>{
        setShowSpinner(true);
        const requestRef = doc(db, "requests", props.request.id);
        runTransaction(db, async (transaction)=>{
            // load, save new, delete
            const requestDoc = await transaction.get(requestRef);

            if(!requestDoc.exists()){
                throw new Error("Request document does not exist");
            }
            let tempData = {...requestDoc.data(),
                status: 5,
                print_invoice_number : printInvoiceNumber,
                print_quote_number: printQuoteNumber,
                print_materials: printMaterials,
                print_material_charges: printMaterialCharges,
                print_truck_charge: printTruckCharge,
                print_hours: printHours,
            }

            let techDocs = [];
            if(requestDoc?.data()?.work_order?.assigned_techs !== undefined && requestDoc.data().work_order.assigned_techs.length > 0){
                const assignedTechnicians = requestDoc.data().work_order.assigned_techs;
                for(let x=0; x < assignedTechnicians.length; ++x){
                    techDocs[x] = await transaction.get(doc(db, "technicians", assignedTechnicians[x]));
                    if(!techDocs[x].exists()){
                        throw new Error("Technician document does not exist");
                    }
                }
            }

            await transaction.set(doc(db, 'finalized_work_orders/', requestDoc.id), tempData);
            await transaction.delete(requestRef);

            // remove if de-selected the tech
            for(let x=0; x<techDocs.length; ++x){
                let newTechDoc = techDocs[x].data();
                // console.log(newTechDoc);
                const indexOfId = newTechDoc.work_orders.findIndex(e => e.id === requestDoc.id);
                if(indexOfId > -1){
                    newTechDoc.work_orders.splice(indexOfId, 1);
                }// if doesn't exist then don't need to remove
                await transaction.update(doc(db, "technicians", techDocs[x].id), newTechDoc);
            }
            return tempData;
        }).then((newState)=>{
            // clean up
            setShowSpinner(false);
            toast.success(`WO: ${props?.request?.data?.work_order?.order_number} has been finalized.`);
            setTruckChargeWarning("");
            setSubmittedHoursWarning("");
            setTechBlankHoursWarning([]);
            setTechBlankRatesWarning([]);
            setUnitsWarning([]);
            setMaterialsWarning([]);
            setPrintInvoiceNumber(null);
            setPrintQuoteNumber(null);
            setPrintMaterials(null);
            setPrintMaterialCharges(null);
            setPrintTruckCharge(null);
            setPrintHours(null);
            setQuoteNo("");
            setQuoteAmount(0.00);
            setTruckCharge(0.00);
            setHours([]);
            setAssignedTechnicians([]);
            setMaterials([]);
            props.onHide();
        }).catch((error)=>{
            setShowSpinner(false);
            setTruckChargeWarning("");
            setSubmittedHoursWarning("");
            setTechBlankHoursWarning([]);
            setTechBlankRatesWarning([]);
            setUnitsWarning([]);
            setMaterialsWarning([]);
            setPrintInvoiceNumber(null);
            setPrintQuoteNumber(null);
            setPrintMaterials(null);
            setPrintMaterialCharges(null);
            setPrintTruckCharge(null);
            setPrintHours(null);
            setQuoteNo("");
            setQuoteAmount(0.00);
            setTruckCharge(0.00);
            setHours([]);
            setAssignedTechnicians([]);
            setMaterials([]);
            props.onHide('show cancel');
            toast.error(`Failed to save data: ${error.message}`);
        });
    };

    const updateTechBlankHoursWarning = useCallback((tech)=>{
        const techName = props.technicians.find(searchTech=>searchTech.id === tech.techID);
        let newWarning = '';
        if(techName !== undefined){
            newWarning = `Warning - ${props.technicians.find(searchTech=>searchTech.id === tech.techID).data.tech_name}: all hours are blank`;
            setTechBlankHoursWarning((t)=>[...t, newWarning]);
        }
    }, [props.technicians]);

    const updateTechBlankRatesWarning = useCallback((tech, day)=>{
        const techName = props.technicians.find(searchTech=>searchTech.id === tech.techID);
        let newWarning = '';
        if(techName !== undefined){
            newWarning = `Warning - ${props.technicians.find(searchTech=>searchTech.id === tech.techID).data.tech_name}: please fill out the Rate for ${day}`;
            setTechBlankRatesWarning((t)=>[...t, newWarning]);
        }
    }, [props.technicians]);

    const updateUnitsWarning = useCallback((unit, heading)=>{
        let newWarning = `Warning - Unit ${unit}: ${heading} is not filled out`;
        setUnitsWarning((t)=>[...t, newWarning]);
    }, []);

    const updateMaterialsWarning = useCallback((unit, heading)=>{
        let newWarning = `Warning - Materials ${unit}: ${heading} is not filled out`;
        setMaterialsWarning((t)=>[...t, newWarning]);
    }, []);
    
    useEffect(()=>{
        setTruckChargeWarning("");
        setSubmittedHoursWarning("");
        setTechBlankHoursWarning([]);
        setTechBlankRatesWarning([]);
        setUnitsWarning([]);
        setMaterialsWarning([]);

        setTruckCharge(props?.request?.data?.work_order?.truck_charge === undefined ? 0.00 : props.request.data.work_order.truck_charge);
        setQuoteNo(props?.request?.data?.work_order?.quote_number === undefined ? '' : props?.request?.data?.work_order?.quote_number);
        setQuoteAmount(props?.request?.data?.work_order?.quote_amount === undefined ||  props?.request?.data?.work_order?.quote_amount === "" ? 0.00 : props?.request?.data?.work_order?.quote_amount);

        setHours(props?.currentworkorderhours === undefined ?  [] : props.currentworkorderhours);
        setAssignedTechnicians(props?.request?.data?.work_order?.assigned_techs === undefined ? [] : props?.request?.data?.work_order?.assigned_techs);
        setMaterials(props?.request?.data?.work_order?.materials === undefined ? [] : props?.request?.data?.work_order?.materials);
      
        setPrintInvoiceNumber(props?.request?.data?.print_invoice_number === undefined ? false : props?.request?.data?.print_invoice_number);
        setPrintQuoteNumber(props?.request?.data?.print_quote_number === undefined ?
                quoteNo === "" ? false : true 
            : 
                props?.request?.data?.print_quote_number
        );
        setPrintMaterials(props?.request?.data?.print_materials === undefined ?
                quoteNo === "" ? true : true
            : 
                props?.request?.data?.print_materials
        );
        setPrintMaterialCharges(props?.request?.data?.print_material_charges === undefined ?
                quoteNo === "" ? true : false
            : 
                props?.request?.data?.print_material_charges
        );
        setPrintTruckCharge(props?.request?.data?.print_truck_charge === undefined ?
                quoteNo === "" ? true : false
            : 
                props?.request?.data?.print_truck_charge
        );
        setPrintHours(props?.request?.data?.print_hours === undefined ?
                quoteNo === "" ? true : false
            :
                props?.request?.data?.print_hours
        );
    },[props, quoteNo]);
   
    useEffect(()=>{
        if(props.show){
            if(props?.currentworkorderhours !== undefined && props?.request?.data?.work_order?.assigned_techs !== undefined){
                // if work hours array length does not equal work order assigned techs - someone has not submitted hours
                if(props.currentworkorderhours.length < props.request.data.work_order.assigned_techs.length){
                    setSubmittedHoursWarning(`Warning - Only ${props.currentworkorderhours.length} of ${props.request.data.work_order.assigned_techs.length} techs have sumbitted hours`);
                }
                // if work hours array is empty - nobody has submmitted hours
                if(props.currentworkorderhours.length === 0){
                    setSubmittedHoursWarning('Warning - No hours have been submitted');
                }
                // now check each hours and days
                props.currentworkorderhours.forEach((tech)=>{
                     // if each day is an empty string then hours have not been submitted for that user
                    if(
                        tech.Monday === "" &&
                        tech.Tuesday === "" &&
                        tech.Wednesday === "" &&
                        tech.Thursday === "" &&
                        tech.Friday === "" &&
                        tech.Saturday === "" &&
                        tech.Sunday === ""
                    ){
                        updateTechBlankHoursWarning(tech);
                    }
                    // if each day that has hours submitted but the corresponding day rate is an empty string - then rate needs to be filled out
                    if(tech.Monday !== "" && tech["Monday Rate"] === ""){
                        updateTechBlankRatesWarning(tech, "Monday");
                    };
                    if(tech.Tuesday !== "" && tech["Tuesday Rate"] === ""){
                        updateTechBlankRatesWarning(tech, "Tuesday");
                    };
                    if(tech.Wednesday !== "" && tech["Wednesday Rate"] === ""){
                        updateTechBlankRatesWarning(tech, "Wednesday");
                    };
                    if(tech.Thursday !== "" && tech["Thursday Rate"] === ""){
                        updateTechBlankRatesWarning(tech, "Thursday");
                    };
                    if(tech.Friday !== "" && tech["Friday Rate"] === ""){
                        updateTechBlankRatesWarning(tech, "Friday");
                    };
                    if(tech.Saturday !== "" && tech["Saturday Rate"] === ""){
                        updateTechBlankRatesWarning(tech, "Saturday");
                    };
                    if(tech.Sunday !== "" && tech["Sunday Rate"] === ""){
                        updateTechBlankRatesWarning(tech, "Sunday");
                    };

                });

            }else{
                setSubmittedHoursWarning('Warning - No hours have been submitted');
            }
        
            // if truck charge is undefined or null or "" then warn a charge needs to be filled out.
            if(props?.request?.data?.work_order?.truck_charge !== undefined){
                //console.log(props.request.data.work_order.truck_charge)
                if(props.request.data.work_order.truck_charge === 0){
                    setTruckChargeWarning('Warning - Truck Charge is $0.00');
                }
                if(props.request.data.work_order.truck_charge === ""){
                    setTruckChargeWarning('Warning - Truck Charge is empty');
                }
            }else{
                setTruckChargeWarning('Warning - Truck Charge does not exist');
            }
            
            // check each unit has a description of work performed, make, model, serial
            if(props?.request?.data?.work_order?.units !== undefined){
                if(props.request.data.work_order.units.length > 0){
                    props.request.data.work_order.units.forEach((unit, index)=>{
                        if(unit?.Make === undefined || unit.Make === ""){
                            updateUnitsWarning(index+1, 'Make');
                        }
                        if(unit?.Model === undefined || unit.Model === "" ){
                            updateUnitsWarning(index+1, 'Model');
                        }
                        if(unit?.Serial === undefined || unit.Serial === "" ){
                            updateUnitsWarning(index+1, 'Serial');
                        }
                        if(unit?.['Description of Work Performed'] === undefined || unit['Description of Work Performed'] === "" ){
                            updateUnitsWarning(index+1, 'Description of Work Performed');
                        }
                        if(unit?.['Service Recommended'] === undefined || unit['Service Recommended'] === "" ){
                            updateUnitsWarning(index+1, 'Service Recommended');
                        }
                    });
                }else{
                    setUnitsWarning(['Warning - No units specified for this work order']);
                }
            }else{
                setUnitsWarning(['Warning - No units specified for this work order']);
            }

            // check each material has a source, qty, description, and price
            if(props?.request?.data?.work_order?.materials !== undefined){
                if(props.request.data.work_order.materials.length > 0){
                    props.request.data.work_order.materials.forEach((material, index)=>{
                        if(material?.Source === undefined || material.Source === ""){
                            updateMaterialsWarning(index+1, 'Source');
                        }
                        if(material?.Qty === undefined || material.Qty === "" ){
                            updateMaterialsWarning(index+1, 'Qty');
                        }
                        if(material?.Price === undefined || material.Price === "" ){
                            updateMaterialsWarning(index+1, 'Price');
                        }
                        if(material?.['Material Description'] === undefined || material['Material Description'] === "" ){
                            updateMaterialsWarning(index+1, 'Material Description');
                        }
                    });
                }else{
                    setMaterialsWarning(['Warning - No materials specified for this work order']);
                }
            }else{
                setMaterialsWarning(['Warning - No materials specified for this work order']);
            }
        }
    }, [props, updateTechBlankHoursWarning, updateTechBlankRatesWarning, updateUnitsWarning, updateMaterialsWarning]);

    const getTotalHours = () =>{
        let totalLabour = 0.00;
        hours.forEach((tech, index)=>{
          if(assignedTechnicians.includes(tech.techID)){
            
            let monday = tech?.Monday !== undefined && tech?.Monday !== "" ? parseFloat(tech.Monday) : 0;
            let mondayPremium = tech?.Premium_Monday !== undefined && tech?.Premium_Monday !== "" ? parseFloat(tech.Premium_Monday) : 0;
            let tuesday = tech?.Tuesday !== undefined && tech?.Tuesday !== "" ? parseFloat(tech.Tuesday) : 0;
            let tuesdayPremium = tech?.Premium_Tuesday !== undefined && tech?.Premium_Tuesday !== "" ? parseFloat(tech.Premium_Tuesday) : 0;
            let wednesday = tech?.Wednesday !== undefined && tech?.Wednesday !== "" ? parseFloat(tech?.Wednesday) : 0;
            let wednesdayPremium = tech?.Premium_Wednesday !== undefined && tech?.Premium_Wednesday !== "" ? parseFloat(tech?.Premium_Wednesday) : 0;
            let thursday = tech?.Thursday !== undefined && tech?.Thursday !== "" ? parseFloat(tech?.Thursday) : 0;
            let thursdayPremium = tech?.Premium_Thursday !== undefined && tech?.Premium_Thursday !== "" ? parseFloat(tech?.Premium_Thursday) : 0;
            let friday = tech?.Friday !== undefined && tech?.Friday !== "" ? parseFloat(tech?.Friday) : 0;
            let fridayPremium = tech?.Premium_Friday !== undefined && tech?.Premium_Friday !== "" ? parseFloat(tech?.Premium_Friday) : 0;

            let saturday = tech?.Saturday !== undefined && tech?.Saturday !== "" ? parseFloat(tech?.Saturday) : 0;
            let sunday = tech?.Sunday !== undefined && tech?.Sunday !== "" ? parseFloat(tech?.Sunday) : 0;
            let saturdayPremium = tech?.Premium_Saturday !== undefined && tech?.Premium_Saturday !== "" ? parseFloat(tech?.Premium_Saturday) : 0;
            let sundayPremium = tech?.Premium_Sunday !== undefined && tech?.Premium_Sunday !== "" ? parseFloat(tech?.Premium_Sunday) : 0;
            
            let mondayRate = tech?.["Monday Rate"] !== undefined && tech?.["Monday Rate"] !== "" ? parseFloat(tech?.["Monday Rate"]) : 0;
            let tuesdayRate = tech?.["Tuesday Rate"] !== undefined && tech?.["Tuesday Rate"] !== "" ? parseFloat(tech?.["Tuesday Rate"]) : 0;
            let wednesdayRate = tech?.["Wednesday Rate"] !== undefined && tech?.["Wednesday Rate"] !== "" ? parseFloat(tech?.["Wednesday Rate"]) : 0;
            let thursdayRate = tech?.["Thursday Rate"] !== undefined && tech?.["Thursday Rate"] !== "" ? parseFloat(tech?.["Thursday Rate"]) : 0;
            let fridayRate = tech?.["Friday Rate"] !== undefined && tech?.["Friday Rate"] !== "" ? parseFloat(tech?.["Friday Rate"]) : 0;
            let saturdayRate = tech?.["Saturday Rate"] !== undefined && tech?.["Saturday Rate"] !== "" ? parseFloat(tech?.["Saturday Rate"]) : 0;
            let sundayRate = tech?.["Sunday Rate"] !== undefined && tech?.["Sunday Rate"] !== "" ? parseFloat(tech?.["Sunday Rate"]) : 0;

            let mondayPremiumRate = tech?.["Premium Monday Rate"] !== undefined && tech?.["Premium Monday Rate"] !== "" ? parseFloat(tech?.["Premium Monday Rate"]) : 0;
            let tuesdayPremiumRate = tech?.["Premium Tuesday Rate"] !== undefined && tech?.["Premium Tuesday Rate"] !== "" ? parseFloat(tech?.["Premium Tuesday Rate"]) : 0;
            let wednesdayPremiumRate = tech?.["Premium Wednesday Rate"] !== undefined && tech?.["Premium Wednesday Rate"] !== "" ? parseFloat(tech?.["Premium Wednesday Rate"]) : 0;
            let thursdayPremiumRate = tech?.["Premium Thursday Rate"] !== undefined && tech?.["Premium Thursday Rate"] !== "" ? parseFloat(tech?.["Premium Thursday Rate"]) : 0;
            let fridayPremiumRate = tech?.["Premium Friday Rate"] !== undefined && tech?.["Premium Friday Rate"] !== "" ? parseFloat(tech?.["Premium Friday Rate"]) : 0;
            let saturdayPremiumRate = tech?.["Premium Saturday Rate"] !== undefined && tech?.["Premium Saturday Rate"] !== "" ? parseFloat(tech?.["Premium Saturday Rate"]) : 0;
            let sundayPremiumRate = tech?.["Premium Sunday Rate"] !== undefined && tech?.["Premium Sunday Rate"] !== "" ? parseFloat(tech?.["Premium Sunday Rate"]) : 0;
            
            totalLabour += ((monday-mondayPremium) * mondayRate) + (mondayPremium * mondayPremiumRate);
            totalLabour += ((tuesday-tuesdayPremium) * tuesdayRate) + (tuesdayPremium * tuesdayPremiumRate);
            totalLabour += ((wednesday-wednesdayPremium) * wednesdayRate) + (wednesdayPremium * wednesdayPremiumRate);
            totalLabour += ((thursday-thursdayPremium) * thursdayRate) + (thursdayPremium * thursdayPremiumRate);
            totalLabour += ((friday-fridayPremium) * fridayRate) + (fridayPremium * fridayPremiumRate);
            totalLabour += ((saturday-saturdayPremium) * saturdayRate) + (saturdayPremium * saturdayPremiumRate);
            totalLabour += ((sunday-sundayPremium) * sundayRate) + (sundayPremium * sundayPremiumRate);

            // ((parseFloat(tech?.Sunday === "" || tech?.Sunday === undefined ? 0 : tech.Sunday) - parseFloat(tech?.Premium_Sunday === "" || tech?.Premium_Sunday === undefined ? 0 : tech.Premium_Sunday)) * parseFloat(tech?.["Sunday Rate"] === "" || tech?.["Sunday Rate"] === undefined ? 0 : tech["Sunday Rate"])) + (parseFloat(tech?.Premium_Sunday === "" || tech?.Premium_Sunday === undefined ? 0 : tech.Premium_Sunday) * parseFloat(tech?.["Premium Sunday Rate"] === "" || tech?.["Premium Sunday Rate"] === undefined ? 0 : tech["Premium Sunday Rate"]))
          }
        })
        // end tech labour
        // labour charges
         return Math.round(totalLabour*100)/100;
    };
    const getTotalMaterialCost = ()=>{
        let materialTotalCost = 0.00;
        for (let item=materials.length-1; item>=0; --item){
            // materials.forEach((item, index)=>{
            let cost = parseFloat(materials[item].Price);
            if(materials[item]?.Price === undefined){materials[item].Price = "" };
            if(materials[item].Price === ""){ cost = 0 };
            
            materialTotalCost += cost;
            
            let dollars = materials[item].Price.split('.')[0] !== undefined ? materials[item].Price.split('.')[0].toString() : "0";
            if(dollars.length === 0){ dollars = "0" };
            
            let cents = materials[item].Price.split('.')[1] !== undefined ? materials[item].Price.split('.')[1].toString() : "00";
            if(cents.length === 1){ cents = `${cents}0` };
        }
        return materialTotalCost;
    };

    return(
        <Modal
            {...props}
            size="lg"
            animation={false}
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header>
                <Modal.Title>FINALIZE WORK ORDER - {props?.request.data?.work_order?.order_number}</Modal.Title>
                <div onClick={()=>{
                    props.onHide('show cancel')
                }} variant="none">
                    <CustomCloseButton/>
                </div>
            </Modal.Header>
            <Modal.Body>
                <Container fluid style={{marginTop:"20px"}}>
                    <Row>
                        <Col lg="12" style={{border:"1px solid black", padding:"10px", borderRadius:"10px", marginBottom:"5px"}}>
                            <Row style={{marginBottom: "20px"}}><Col>WORK ORDER VERIFICATION</Col></Row>
                            
                            <Row style={{marginLeft:"10px"}}><Col>Units:</Col></Row>
                            <Row style={{marginLeft:"10px", marginRight:"10px", marginBottom:"10px"}}>
                            {
                                unitsWarning.length === 0 ?(
                                    <Row>
                                        <Col style={{margin:10, color:"green"}}>
                                            Good - All submitted unit information is filled out
                                        </Col>
                                    </Row>
                                ):(
                                    unitsWarning.map((unitWarning, index)=>{
                                        return(
                                            <Row key={index}>
                                                <Col style={{margin:10, color:"red"}}>
                                                    {
                                                        unitWarning
                                                    }
                                                </Col>
                                            </Row>
                                        )
                                    })
                                )
                            }
                            </Row>

                            <Row style={{marginLeft:"10px"}}><Col>Materials:</Col></Row>
                            <Row style={{marginLeft:"10px", marginRight:"10px", marginBottom:"10px"}}>
                            {
                                materialsWarning.length === 0 ?(
                                    <Row>
                                        <Col style={{margin:10, color:"green"}}>
                                            Good - All submitted materials information is filled out
                                        </Col>
                                    </Row>
                                ):(
                                    materialsWarning.map((materialWarning, index)=>{
                                        return(
                                            <Row key={index}>
                                                <Col style={{margin:10, color:"red"}}>
                                                    {
                                                        materialWarning
                                                    }
                                                </Col>
                                            </Row>
                                        )
                                    })
                                )
                            }
                            </Row>

                            <Row style={{marginLeft:"10px"}}><Col>Truck Charge:</Col></Row>
                            <Row style={{marginLeft:"10px", marginRight:"10px", marginBottom:"10px"}}>
                            {
                                truckChargeWarning === "" ?(
                                    <Col style={{margin:10, color:"green"}}>
                                        Good - Filled out
                                    </Col>
                                ):(
                                    <Col style={{margin:10, color:"red"}}>
                                        {
                                            truckChargeWarning
                                        }
                                    </Col>
                                )
                            }
                            </Row>
                            <Row style={{marginLeft:"10px"}}><Col>Technician Hours:</Col></Row>
                            <Row style={{marginLeft:"10px", marginRight:"10px", marginBottom:"10px"}}>
                            {
                                submittedHoursWarning === "" ?(
                                    <Col style={{margin:10, color:"green"}}>
                                        Good - All assigned Techs have submitted hours
                                    </Col>
                                ):(
                                    <Col style={{margin:10, color:"red"}}>
                                        {
                                            submittedHoursWarning
                                        }
                                    </Col>
                                )
                            }
                            {
                                techBlankHoursWarning.length === 0 ?(
                                    <Row>
                                        <Col style={{margin:10, color:"green"}}>
                                            Good - All submitted Techs have at least one day filled out
                                        </Col>
                                    </Row>
                                ):(
                                    techBlankHoursWarning.map((blankHoursWarning, index)=>{
                                        return(
                                            <Row key={index}>
                                                <Col style={{margin:10, color:"red"}}>
                                                    {
                                                        blankHoursWarning
                                                    }
                                                </Col>
                                            </Row>
                                        )
                                    })
                                )
                            }
                            {
                                techBlankRatesWarning.length === 0 ?(
                                    <Row>
                                        <Col style={{margin:10, color:"green"}}>
                                            Good - All submitted hours have Rates filled out
                                        </Col>
                                    </Row>
                                ):(
                                    techBlankRatesWarning.map((blankRatesWarning, index)=>{
                                        return(
                                            <Row key={index}>
                                                <Col style={{margin:10, color:"red"}}>
                                                    {
                                                        blankRatesWarning
                                                    }
                                                </Col>
                                            </Row>
                                        )
                                    })
                                )
                            }
                            </Row>
                        </Col>

                        <Col lg="12" style={{border:"1px solid black", padding:"10px", borderRadius:"10px", marginBottom:"5px"}}>
                            <Row style={{marginBottom: "20px"}}><Col>PRINT OPTIONS</Col></Row>
                            <Row style={{marginLeft:"15px"}}>
                                <Form.Check
                                    type="checkbox"
                                    id="invoice-number"
                                    label="Invoice Number"
                                    checked={printInvoiceNumber}
                                    onChange={()=>{
                                        setPrintInvoiceNumber(!printInvoiceNumber);
                                    }}
                                />
                            </Row>
                            <Row style={{marginLeft:"15px"}}>
                                <Form.Check
                                    type="checkbox"
                                    id="Quote-number"
                                    label={`Quote Number: ${quoteNo} AMT: $${quoteAmount}`}
                                    checked={printQuoteNumber}
                                    onChange={()=>{
                                        setPrintQuoteNumber(!printQuoteNumber);
                                    }}
                                />
                            </Row>
                            <Row style={{marginLeft:"15px"}}>
                                <Form.Check
                                    type="checkbox"
                                    id="materials"
                                    label="Materials"
                                    checked={printMaterials}
                                    onChange={()=>{
                                        if(printMaterials === true){
                                            setPrintMaterialCharges(false);
                                        }else{
                                            setPrintMaterialCharges(true);
                                        }
                                        setPrintMaterials(!printMaterials);
                                    }}
                                />
                            </Row>
                            {
                                printMaterials &&
                                <Row style={{marginLeft:"25px"}}>
                                    <Form.Check
                                        type="checkbox"
                                        id="material-charges"
                                        label="Material Charges"
                                        checked={printMaterialCharges}
                                        onChange={()=>{
                                            setPrintMaterialCharges(!printMaterialCharges);
                                        }}
                                    />
                                </Row>
                            }
                            <Row style={{marginLeft:"15px"}}>
                                <Form.Check
                                    type="checkbox"
                                    id="truck-charge"
                                    label="Truck Charge"
                                    checked={printTruckCharge}
                                    onChange={()=>{
                                        setPrintTruckCharge(!printTruckCharge);
                                    }}
                                />
                            </Row>
                            <Row style={{marginLeft:"15px"}}>
                                <Form.Check
                                    type="checkbox"
                                    id="hours"
                                    label="Labour"
                                    checked={printHours}
                                    onChange={()=>{
                                        setPrintHours(!printHours);
                                    }}
                                />
                            </Row>
                            <Row style={{marginLeft:"15px"}}>
                                Total on Invoice: ${
                                    0 + 
                                    (printQuoteNumber ? quoteAmount : 0) +
                                    (printTruckCharge ? truckCharge : 0) +
                                    (printHours ? getTotalHours() : 0) +
                                    (printMaterialCharges ? getTotalMaterialCost() : 0)
                                }
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Button 
                    className="btn-dark"
                    style={{backgroundColor:"#2596be"}}
                    onClick={()=>{
                        if(
                            truckChargeWarning !== "" ||
                            submittedHoursWarning !== "" ||
                            techBlankHoursWarning.length !== 0 ||
                            techBlankRatesWarning.length !== 0 ||
                            unitsWarning.length !== 0 ||
                            materialsWarning.length !== 0
                        ){
                            if(window.confirm(`Are you sure you want to finalize WO: ${props?.request?.data?.work_order?.order_number} with warnings?`)){
                                finalize();
                            }
                        }else{
                            finalize();
                        }
                        
                    }}
                >
                    Finalize
                </Button>
                <Button 
                    className="btn-dark"
                    style={{backgroundColor:"#2596be"}}
                    onClick={()=>{
                        props.onHide('show cancel');
                    }}
                >
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    )
};

export default FinalizeRequestModal;